import React, { useState, useEffect, useCallback } from "react";
import { api, formatCOP, formatApiError } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

function KpiCard({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-2">{label}</p>
      <p className="font-serif text-3xl text-gray-900">{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

function pct(num, denom) {
  if (!denom || denom === 0) return "—";
  const v = ((num || 0) / denom) * 100;
  return `${v.toFixed(0)}%`;
}

export default function AdminCostsPage() {
  const [data, setData] = useState(null);
  const [since, setSince] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = since ? `?since=${since}` : "";
      const { data: d } = await api.get(`/admin/costs${params}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando costos"); }
    finally { setLoading(false); }
  }, [since]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-costs-page">
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Operación</p>
      <h1 className="font-serif text-3xl text-gray-900 mb-1">Costos & uso</h1>
      <p className="text-xs text-gray-500 mb-5">LLM, emails, SECOP y almacenamiento PDF.</p>

      <div className="flex items-end gap-2 mb-6 flex-wrap">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Desde</label>
          <Input type="date" value={since} onChange={(e) => setSince(e.target.value)} className="rounded-sm h-9" data-testid="costs-from" />
        </div>
        <Button variant="outline" className="rounded-sm h-9" onClick={() => setSince("")} data-testid="costs-clear">Sin filtro</Button>
        <Button variant="outline" className="rounded-sm h-9" onClick={load} data-testid="costs-refresh">Refrescar</Button>
      </div>

      {loading && <p className="text-sm text-gray-500">Cargando…</p>}

      {!loading && data && (
        <>
          <section className="mb-8">
            <h2 className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">Pagos & Ingresos</h2>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-4">
              <KpiCard 
                label="Ingresos reales (N1+N2)" 
                value={`${formatCOP(data.payments?.total_real_revenue_cop || 0)}`} 
                sub={`Pagos confirmados por usuarios`}
                testid="kpi-payments-real" 
              />
              <KpiCard 
                label="Obsequios entregados (N1+N2)" 
                value={`${formatCOP(data.payments?.total_complimentary_given_cop || 0)}`} 
                sub={`Pagos marcados como obsequio`}
                testid="kpi-payments-complimentary" 
              />
            </div>
            {data.payments?.breakdown && (
              <div className="bg-white border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
                      <th className="px-3 py-3 text-left">Nivel</th>
                      <th className="px-3 py-3 text-right">Pagos reales</th>
                      <th className="px-3 py-3 text-right">Monto real</th>
                      <th className="px-3 py-3 text-right">Obsequios</th>
                      <th className="px-3 py-3 text-right">Monto obsequios</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.payments.breakdown).map(([nivel, stats]) => (
                      <tr key={nivel} className="border-b border-gray-100" data-testid={`payments-breakdown-${nivel}`}>
                        <td className="px-3 py-2 text-xs font-medium text-gray-900">{nivel}</td>
                        <td className="px-3 py-2 text-right text-xs">{stats.real_count}</td>
                        <td className="px-3 py-2 text-right text-xs">{formatCOP(stats.real_amount_cop)}</td>
                        <td className="px-3 py-2 text-right text-xs">{stats.complimentary_count}</td>
                        <td className="px-3 py-2 text-right text-xs">{formatCOP(stats.complimentary_amount_cop)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="mb-8">
            <h2 className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">LLM (Diagnósticos)</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KpiCard label="Total estimado" value={`USD ${data.llm.total_usd.toFixed(4)}`} testid="kpi-llm-total" />
              <KpiCard label="Generaciones" value={data.llm.generations} testid="kpi-llm-count" />
              <KpiCard label="Promedio / generación" value={`USD ${data.llm.avg_usd.toFixed(4)}`} testid="kpi-llm-avg" />
            </div>
          </section>

          <section className="mb-8" data-testid="llm-by-prompt-section">
            <h2 className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">LLM por prompt (A/B granular)</h2>
            {(data.llm.by_prompt || []).length === 0 ? (
              <p className="text-sm text-gray-500">Sin datos de A/B prompts aún en este rango.</p>
            ) : (
              <div className="bg-white border border-gray-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
                      <th className="px-3 py-3 text-left">Prompt key</th>
                      <th className="px-3 py-3 text-right">Llamadas</th>
                      <th className="px-3 py-3 text-right">Costo total (USD)</th>
                      <th className="px-3 py-3 text-right">Costo medio</th>
                      <th className="px-3 py-3 text-right">Latencia media (ms)</th>
                      <th className="px-3 py-3 text-right">Tokens in/out</th>
                      <th className="px-3 py-3 text-right">% Éxito</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.llm.by_prompt.map((row) => (
                      <tr key={row.prompt_key} className="border-b border-gray-100" data-testid={`llm-prompt-row-${row.prompt_key}`}>
                        <td className="px-3 py-2 text-xs font-mono text-gray-900">{row.prompt_key}</td>
                        <td className="px-3 py-2 text-right text-xs">{row.calls}</td>
                        <td className="px-3 py-2 text-right text-xs font-medium">${row.total_cost_usd.toFixed(4)}</td>
                        <td className="px-3 py-2 text-right text-xs">${row.avg_cost_usd.toFixed(6)}</td>
                        <td className="px-3 py-2 text-right text-xs">{row.avg_latency_ms.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right text-xs text-gray-600">
                          {row.tokens_in_sum.toLocaleString()} / {row.tokens_out_sum.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right text-xs">
                          <span className={row.success_rate_pct >= 95 ? "text-emerald-700" : row.success_rate_pct >= 80 ? "text-amber-700" : "text-rose-700"}>
                            {row.success_rate_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-2">
              Datos de <code>prompt_ab_results</code>. Incluye éxitos y errores. La latencia y tokens son agregados de todas las variantes activas.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">Emails transaccionales</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Total" value={data.emails.total} testid="kpi-email-total" />
              <KpiCard label="Enviados" value={data.emails.sent} sub="status=sent" testid="kpi-email-sent" />
              <KpiCard label="Fallidos" value={data.emails.failed} sub="status=failed" testid="kpi-email-failed" />
              <KpiCard label="Skipped" value={data.emails.skipped} sub="sandbox / sin key" testid="kpi-email-skipped" />
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">SECOP</h2>
            <div className="grid grid-cols-2 gap-3">
              <KpiCard label="Búsquedas" value={data.secop.searches} testid="kpi-secop-searches" />
              <KpiCard label="Fallos API" value={data.secop.failures} sub="audit log: secop.*fail" testid="kpi-secop-fails" />
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">Prevalidaciones (Fase 12)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <KpiCard
                label="Total prevalidaciones"
                value={data.prevalidations?.total ?? 0}
                sub="Todas las versiones generadas"
                testid="kpi-prevalidations-total"
              />
              <KpiCard
                label="Cuota usuarios activos (mes)"
                value={data.quotas?.active_users ?? 0}
                sub={data.quotas?.current_month || "—"}
                testid="kpi-quotas-active"
              />
              <KpiCard
                label="Prevalid. usadas este mes"
                value={data.quotas?.total_prevalidations_used_this_month ?? 0}
                sub="Suma de todos los usuarios"
                testid="kpi-quotas-used"
              />
              <KpiCard
                label="Overrides admin"
                value={data.quotas?.admin_overrides_count ?? 0}
                sub="Cuotas ampliadas manualmente"
                testid="kpi-quotas-overrides"
              />
            </div>
            {(data.prevalidations?.by_month_recent || []).length > 0 && (
              <div className="bg-white border border-gray-200" data-testid="prevalidations-by-month">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 px-3 py-2 border-b">Prevalidaciones por mes (recientes)</p>
                <table className="w-full text-sm">
                  <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
                    <th className="px-3 py-2 text-left">Mes</th>
                    <th className="px-3 py-2 text-right">Cantidad</th>
                  </tr></thead>
                  <tbody>
                    {data.prevalidations.by_month_recent.map((row) => (
                      <tr key={row.year_month} className="border-b border-gray-100">
                        <td className="px-3 py-2 text-xs font-mono">{row.year_month}</td>
                        <td className="px-3 py-2 text-right text-xs">{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="mb-8">
            <h2 className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">Diagnósticos & Kits (variantes)</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KpiCard
                label="Diagnósticos total"
                value={data.diagnoses?.total ?? 0}
                sub={`Auto: ${data.diagnoses?.by_variant?.auto ?? 0} · Reviewed: ${data.diagnoses?.by_variant?.reviewed ?? 0}`}
                testid="kpi-diagnoses-total"
              />
              <KpiCard
                label="Kits N2 total"
                value={data.kits?.total ?? 0}
                sub={`Auto: ${data.kits?.by_variant?.auto ?? 0} · Reviewed: ${data.kits?.by_variant?.reviewed ?? 0}`}
                testid="kpi-kits-total"
              />
              <div className="bg-white border border-gray-200 p-5">
                <p className="text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-2">Tasa auto vs reviewed</p>
                <p className="text-xs text-gray-700">
                  N1 — Auto: {pct(data.diagnoses?.by_variant?.auto, data.diagnoses?.total)} ·
                  Reviewed: {pct(data.diagnoses?.by_variant?.reviewed, data.diagnoses?.total)}
                </p>
                <p className="text-xs text-gray-700 mt-1">
                  N2 — Auto: {pct(data.kits?.by_variant?.auto, data.kits?.total)} ·
                  Reviewed: {pct(data.kits?.by_variant?.reviewed, data.kits?.total)}
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xs uppercase tracking-[0.22em] text-gray-500 mb-3">Almacenamiento (PDFs aprobados)</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <KpiCard label="Espacio total" value={`${data.storage.total_mb} MB`} sub={`${data.storage.total_bytes.toLocaleString()} bytes`} testid="kpi-storage-total" />
              <KpiCard label="Documentos" value={data.storage.documents} testid="kpi-storage-count" />
              <div className="bg-white border border-gray-200 p-5">
                <p className="text-[10px] uppercase tracking-[0.22em] text-gray-500 mb-2">Por nivel</p>
                {Object.entries(data.storage.by_nivel || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-sm border-b border-gray-100 py-1" data-testid={`storage-nivel-${k}`}>
                    <span className="font-mono text-[11px] text-gray-600">{k}</span>
                    <span>{(v.bytes / (1024 * 1024)).toFixed(2)} MB · {v.count} docs</span>
                  </div>
                ))}
                {Object.keys(data.storage.by_nivel || {}).length === 0 && <p className="text-sm text-gray-500">Sin documentos.</p>}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
